import { BaseDataProvider } from "./baseProvider";
import type { BookingListParams } from "../types/api";

export class BookingDataProvider extends BaseDataProvider {
  constructor() {
    super("bookings");
  }

  async getList(_resource: string, params: BookingListParams) {
    const { page, perPage } = params.pagination || { page: 1, perPage: 20 };
    const { field, order } = params.sort || {
      field: "createdAt",
      order: "DESC",
    };
    const filter = params.filter || {};

    // Build query parameters for bookings API
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: perPage.toString(),
      ...(field && { sortBy: field }),
      ...(order && { sortOrder: order.toUpperCase() }),
      ...(filter.search && { search: filter.search }),
      ...(filter.status && { status: filter.status }),
      ...(filter.userId && { userId: filter.userId }),
      ...(filter.propertyHid && {
        propertyHid: filter.propertyHid.toString(),
      }),
      ...(filter.createdFrom && { createdFrom: filter.createdFrom }),
      ...(filter.createdTo && { createdTo: filter.createdTo }),
      ...(filter.createdWithinDays && {
        createdWithinDays: filter.createdWithinDays.toString(),
      }),
      ...(filter.amountB2B2CMin && {
        amountB2B2CMin: filter.amountB2B2CMin.toString(),
      }),
      ...(filter.amountB2B2CMax && {
        amountB2B2CMax: filter.amountB2B2CMax.toString(),
      }),
      ...(filter.ourProfitMin && {
        ourProfitMin: filter.ourProfitMin.toString(),
      }),
      ...(filter.ourProfitMax && {
        ourProfitMax: filter.ourProfitMax.toString(),
      }),
      ...(filter.amountToPayProviderMin && {
        amountToPayProviderMin: filter.amountToPayProviderMin.toString(),
      }),
      ...(filter.amountToPayProviderMax && {
        amountToPayProviderMax: filter.amountToPayProviderMax.toString(),
      }),
    });

    const json = await this.makeRequest(`bookings?${queryParams}`);

    // Handle different possible API response structures
    const apiResponse = json as {
      data?: unknown[];
      bookings?: unknown[];
      total?: number;
      totalCount?: number;
      pageInfo?: {
        hasNextPage: boolean;
        hasPreviousPage: boolean;
      };
    };

    // Use bookings array if data array is not available
    const dataArray = apiResponse.data || apiResponse.bookings || [];
    const total = apiResponse.total || apiResponse.totalCount || 0;

    return {
      data: dataArray,
      total: total,
      pageInfo: {
        hasNextPage: apiResponse.pageInfo?.hasNextPage || false,
        hasPreviousPage: apiResponse.pageInfo?.hasPreviousPage || false,
      },
    };
  }

  async getOne(_resource: string, params: Record<string, unknown>) {
    const endpoint = `bookings/${params.id}`;
    const response = await this.makeRequest(endpoint);
    return { data: response };
  }

  async getMany(resource: string, params: Record<string, unknown>) {
    // For getMany, we'll fetch each booking individually
    const promises = (params.ids as string[]).map((id) =>
      this.getOne(resource, { id }),
    );
    const results = await Promise.all(promises);
    return { data: results.map((result) => result.data) };
  }

  async getManyReference() {
    // This would typically be used for related resources
    return {
      data: [],
      total: 0,
      pageInfo: {
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
  }

  // Bookings are read-only, so we don't implement create, update, or delete
  async create() {
    throw new Error("Creating bookings is not supported");
  }

  async update() {
    throw new Error("Updating bookings is not supported");
  }

  async updateMany() {
    throw new Error("Updating bookings is not supported");
  }

  async delete() {
    throw new Error("Deleting bookings is not supported");
  }

  async deleteMany() {
    throw new Error("Deleting bookings is not supported");
  }
}
